import { useEffect, useRef, useState } from 'react';

/*
 * The Solutions page's working infographics.
 *
 * The page described a product in cards and key/value tables, which is how a
 * brochure reads. Each piece here keeps the same facts and lets the visitor do
 * the thing instead: record a payment and watch what follows, move a repair
 * along, ask the assistant a question, slide the portfolio size.
 *
 * All the figures belong to one illustrative demo portfolio, so the numbers
 * agree with each other from one section to the next. Nothing animates under
 * prefers-reduced-motion, and every control is a real button.
 */

function useReducedMotion(): boolean {
  const [r, setR] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return r;
}

/** True once the element has been on screen. It does not go back to false. */
function useSeen<T extends Element>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen] as const;
}

const fmt = (n: number, d = 0) => n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

export function CountUp({ to, decimals = 0, prefix = '', suffix = '', ms = 1100 }: {
  to: number; decimals?: number; prefix?: string; suffix?: string; ms?: number;
}) {
  const reduced = useReducedMotion();
  const [ref, seen] = useSeen<HTMLSpanElement>(0.4);
  const [v, setV] = useState(reduced ? to : 0);
  useEffect(() => {
    if (!seen) return;
    if (reduced) { setV(to); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, to, ms, reduced]);
  return <span ref={ref}>{prefix}{fmt(v, decimals)}{suffix}</span>;
}

/** A photograph that quietly steps aside if the file is not there. */
export function Photo({ src, alt, caption, className = '' }: { src: string; alt: string; caption?: string; className?: string }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return (
    <figure className={`sx-photo ${className}`}>
      <img src={src} alt={alt} loading="lazy" decoding="async" width={1536} height={1024} onError={() => setOk(false)} />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Journey: Manage to Act                                             */
/* ------------------------------------------------------------------ */

export function JourneyStrip({ stops }: { stops: { label: string; live: boolean; text: string }[] }) {
  const [i, setI] = useState(0);
  const cur = stops[i]!;
  return (
    <div className="sx-journey">
      <ol className="sx-journey-bar" role="tablist" aria-label="From managing to acting">
        {stops.map((s, n) => (
          <li key={s.label}>
            <button type="button" role="tab" aria-selected={n === i}
              className={`sx-stop ${n === i ? 'on' : ''} ${s.live ? 'live' : 'soon'} ${n < i ? 'past' : ''}`}
              onClick={() => setI(n)}>
              <span className="dot" aria-hidden>{s.live ? n + 1 : ''}</span>
              <b>{s.label}</b>
              <small>{s.live ? 'Live' : 'Being built'}</small>
            </button>
          </li>
        ))}
      </ol>
      <p className={`sx-journey-text ${cur.live ? '' : 'soon'}`} key={cur.label} role="tabpanel">{cur.text}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Coverage: the five categories                                      */
/* ------------------------------------------------------------------ */

export function CoverageExplorer({ cats }: { cats: { name: string; items: { label: string; soon?: true }[] }[] }) {
  const [i, setI] = useState(0);
  const cur = cats[i]!;
  const live = cur.items.filter((x) => !x.soon).length;
  const total = cats.reduce((n, c) => n + c.items.length, 0);
  const soonAll = cats.reduce((n, c) => n + c.items.filter((x) => x.soon).length, 0);
  return (
    <div className="sx-cover">
      <div className="sx-cover-tabs" role="tablist" aria-label="Areas the platform covers">
        {cats.map((c, n) => (
          <button key={c.name} type="button" role="tab" aria-selected={n === i} className={`sx-tab ${n === i ? 'on' : ''}`} onClick={() => setI(n)}>
            {c.name}
          </button>
        ))}
      </div>
      <div className="sx-cover-panel" role="tabpanel" key={cur.name}>
        <div className="sx-cover-ring" aria-hidden>
          <svg viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="32" className="bg" />
            <circle cx="40" cy="40" r="32" className="fg" pathLength={1} style={{ strokeDasharray: `${live / cur.items.length} 1` }} />
          </svg>
          <b>{live}<small>/{cur.items.length}</small></b>
        </div>
        <div>
          <h3>{cur.name}</h3>
          <p className="sx-muted">{live} of {cur.items.length} live today{live < cur.items.length ? ', the rest on the way' : ''}.</p>
          <ul className="sx-chips">
            {cur.items.map((it, n) => (
              <li key={it.label} className={it.soon ? 'soon' : ''} style={{ ['--i' as string]: n }}>
                {it.label}{it.soon && <em>Coming soon</em>}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="sx-muted sx-center">{total - soonAll} of {total} items across the five areas are live today.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Properties as rings                                                */
/* ------------------------------------------------------------------ */

export function PropertyRings({ props }: { props: { name: string; units: number; occupied: number }[] }) {
  const [ref, seen] = useSeen<HTMLDivElement>(0.3);
  const [sel, setSel] = useState(0);
  const p = props[sel]!;
  const pct = Math.round((p.occupied / p.units) * 100);
  return (
    <div className="sx-card sx-props" ref={ref}>
      <div className="sx-props-list" role="tablist" aria-label="Properties">
        {props.map((x, n) => {
          const o = Math.round((x.occupied / x.units) * 100);
          return (
            <button key={x.name} type="button" role="tab" aria-selected={n === sel} className={`sx-ring-btn ${n === sel ? 'on' : ''}`} onClick={() => setSel(n)}>
              <svg viewBox="0 0 44 44" aria-hidden>
                <circle cx="22" cy="22" r="17" className="bg" />
                <circle cx="22" cy="22" r="17" className="fg" pathLength={1} style={{ strokeDasharray: `${seen ? o / 100 : 0} 1` }} />
              </svg>
              <span><b>{x.name}</b><small>{x.units} units, {o}% occupied</small></span>
            </button>
          );
        })}
      </div>
      <div className="sx-props-units" role="tabpanel" key={p.name}>
        <p className="sx-muted">{p.name}: {p.occupied} of {p.units} units occupied ({pct}%)</p>
        <div className="sx-units">
          {Array.from({ length: p.units }, (_, n) => (
            <span key={n} className={n < p.occupied ? 'occ' : 'vac'} style={{ ['--i' as string]: n }}>
              {n < p.occupied ? 'In' : 'Empty'}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Record a payment and watch what follows                            */
/* ------------------------------------------------------------------ */

const PAY_STEPS: { label: string; out: string }[] = [
  { label: 'Rent due', out: 'October rent of KES 16,250 is open for J. Mutua.' },
  { label: 'Payment received', out: 'KES 16,250 recorded against the tenancy, by M-Pesa reference.' },
  { label: 'Recorded', out: 'Allocated to the October charge, which is now cleared.' },
  { label: 'Receipt issued', out: 'Receipt RCP-000003, numbered in sequence.' },
  { label: 'Tenant notified', out: 'Confirmation sent to the tenant by SMS.' },
  { label: 'Balance updated', out: 'Tenant balance KES 0. Arrears fall by KES 16,250.' },
];

export function PaymentDemo() {
  const reduced = useReducedMotion();
  const [at, setAt] = useState(0);
  const [run, setRun] = useState(false);
  useEffect(() => {
    if (!run) return;
    if (reduced) { setAt(PAY_STEPS.length); setRun(false); return; }
    if (at >= PAY_STEPS.length) { setRun(false); return; }
    const t = window.setTimeout(() => setAt((n) => n + 1), 650);
    return () => window.clearTimeout(t);
  }, [run, at, reduced]);

  const paid = at >= 3;
  const collected = 475500 + (paid ? 16250 : 0);
  const out = 49500 - (paid ? 16250 : 0);
  const rate = (collected / 525000) * 100;
  const start = () => { setAt(1); setRun(true); };

  return (
    <div className="sx-card sx-pay">
      <ol className="sx-steps" aria-label="What happens when a payment is recorded">
        {PAY_STEPS.map((s, n) => (
          <li key={s.label} className={n < at ? 'done' : n === at && run ? 'now' : ''}>
            <span className="n" aria-hidden>{n < at ? '✓' : n + 1}</span>{s.label}
          </li>
        ))}
      </ol>
      <div className="sx-pay-body">
        <div className="sx-pay-meter" aria-live="polite">
          <div className="sx-pay-row"><span>Expected</span><b>KES 525,000</b></div>
          <div className="sx-pay-row"><span>Collected</span><b className="pos">KES {fmt(collected)}</b></div>
          <div className="sx-pay-row"><span>Outstanding this period</span><b className="neg">KES {fmt(out)}</b></div>
          <div className="sx-bar" role="img" aria-label={`Collection rate ${rate.toFixed(1)} percent`}><i style={{ width: `${rate}%` }} /></div>
          <div className="sx-pay-rate">{rate.toFixed(2)}% collected</div>
        </div>
        <ul className="sx-pay-log" aria-live="polite">
          {PAY_STEPS.slice(0, at).map((s) => <li key={s.label}><b>{s.label}</b>{s.out}</li>)}
          {at === 0 && <li className="idle">Press the button to record a payment and follow it through.</li>}
        </ul>
      </div>
      <div className="sx-pay-actions">
        <button type="button" className="sx-btn" disabled={run} onClick={start}>
          {at >= PAY_STEPS.length ? 'Record it again' : 'Record J. Mutua’s KES 16,250'}
        </button>
        {at > 0 && !run && <button type="button" className="sx-btn ghost" onClick={() => setAt(0)}>Reset</button>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Arrears by age                                                     */
/* ------------------------------------------------------------------ */

const BANDS: { label: string; who: [string, number][] }[] = [
  { label: '1 to 7 days', who: [['A. Wanjiru', 12000], ['P. Njoroge', 6800]] },
  { label: '8 to 30 days', who: [['R. Kamau', 33250], ['J. Mutua', 16250]] },
  { label: '31 to 60 days', who: [['S. Achieng', 22000]] },
  { label: 'Over 60 days', who: [['T. Mwangi', 62000]] },
];

export function ArrearsBands() {
  const [ref, seen] = useSeen<HTMLDivElement>(0.3);
  const [sel, setSel] = useState(1);
  const totals = BANDS.map((b) => b.who.reduce((n, w) => n + w[1], 0));
  const max = Math.max(...totals);
  const cur = BANDS[sel]!;
  return (
    <div className="sx-card sx-arr" ref={ref}>
      <div className="sx-arr-head"><b>Arrears by age</b><span className="sx-pill neg">KES {fmt(totals.reduce((a, b) => a + b, 0))}</span></div>
      <div className="sx-arr-bars" role="tablist" aria-label="Arrears bands">
        {BANDS.map((b, n) => (
          <button key={b.label} type="button" role="tab" aria-selected={n === sel} className={`sx-arr-row ${n === sel ? 'on' : ''} a${n}`} onClick={() => setSel(n)}>
            <span className="l">{b.label}</span>
            <span className="t"><i style={{ width: seen ? `${(totals[n]! / max) * 100}%` : 0 }} /></span>
            <span className="v">{fmt(totals[n]!)}</span>
          </button>
        ))}
      </div>
      <ul className="sx-arr-who" role="tabpanel" key={cur.label}>
        {cur.who.map(([n, v]) => <li key={n}><span>{n}</span><b>KES {fmt(v)}</b></li>)}
      </ul>
      <p className="sx-muted sx-note">Illustrative demo portfolio. The oldest debt sits at the bottom, so it is chased first.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Maintenance board                                                  */
/* ------------------------------------------------------------------ */

const REPAIR_STAGES = ['Reported', 'Assigned', 'In progress', 'Completed', 'Closed'];

export function MaintenanceBoard() {
  const [st, setSt] = useState(2);
  return (
    <div className="sx-card sx-board">
      <div className="sx-board-cols">
        {REPAIR_STAGES.map((s, n) => (
          <button key={s} type="button" className={`sx-col ${n === st ? 'on' : ''} ${n < st ? 'past' : ''}`} onClick={() => setSt(n)} aria-pressed={n === st}>
            <span className="h">{s}</span>
            <span className="slot">{n === st && <span className="sx-ticket" key={st}><b>Kitchen tap, A04</b><small>John, plumber</small><small>Est. KES 3,500</small></span>}</span>
          </button>
        ))}
      </div>
      <div className="sx-pay-actions">
        <button type="button" className="sx-btn" disabled={st >= REPAIR_STAGES.length - 1} onClick={() => setSt((n) => Math.min(n + 1, REPAIR_STAGES.length - 1))}>
          {st >= REPAIR_STAGES.length - 1 ? 'Closed' : `Move to ${REPAIR_STAGES[st + 1]!.toLowerCase()}`}
        </button>
        {st !== 2 && <button type="button" className="sx-btn ghost" onClick={() => setSt(2)}>Reset</button>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Charts: income against expenses, and the collection trend          */
/* ------------------------------------------------------------------ */

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
const COLLECTED = [410, 425, 440, 452, 468, 475.5];
const EXPENSES = [120, 134, 98, 142, 125, 136.4];
const RATE = [82.1, 83.6, 85.2, 87.4, 89.2, 90.57];

export function MoneyChart({ kind }: { kind: 'pair' | 'line' }) {
  const [ref, seen] = useSeen<HTMLDivElement>(0.3);
  const [sel, setSel] = useState(5);
  const W = 480, H = 210, L = 12, B = 28, T = 14, step = (W - L * 2) / MONTHS.length;
  const maxV = kind === 'pair' ? 520 : 100, minV = kind === 'pair' ? 0 : 70;
  const y = (v: number) => T + (H - T - B) * (1 - (v - minV) / (maxV - minV));
  const pts = RATE.map((v, n) => [L + step * n + step / 2, y(v)] as const);
  const path = pts.map((p, n) => `${n ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const net = (n: number) => COLLECTED[n]! - EXPENSES[n]!;
  const since = RATE[5]! - RATE[2]!;
  return (
    <div className="sx-card sx-chart" ref={ref}>
      <div className="sx-arr-head">
        <b>{kind === 'pair' ? 'Income against expenses' : 'Collection trend'}</b>
        <span className="sx-pill pos">{kind === 'pair' ? `Net KES ${fmt(net(5) * 1000)}` : `Up ${since.toFixed(1)} points since Jun`}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className={`sx-svg ${seen ? 'go' : ''}`} role="img"
        aria-label={kind === 'pair' ? 'Collected rent and expenses for six months' : 'Rent collection rate for six months'}>
        {[0.25, 0.5, 0.75, 1].map((g) => <line key={g} x1={L} x2={W - L} y1={T + (H - T - B) * (1 - g)} y2={T + (H - T - B) * (1 - g)} className="grid" />)}
        {kind === 'pair' ? MONTHS.map((m, n) => {
          const x = L + step * n;
          return (
            <g key={m} className={n === sel ? 'on' : ''} tabIndex={0} onMouseEnter={() => setSel(n)} onFocus={() => setSel(n)} onClick={() => setSel(n)}
              aria-label={`${m}: collected ${COLLECTED[n]} thousand, expenses ${EXPENSES[n]} thousand`}>
              <rect x={x} y={T} width={step} height={H - T - B} className="hit" />
              <rect x={x + step * 0.16} width={step * 0.3} y={y(COLLECTED[n]!)} height={H - B - y(COLLECTED[n]!)} className="bar c" style={{ ['--i' as string]: n }} />
              <rect x={x + step * 0.52} width={step * 0.3} y={y(EXPENSES[n]!)} height={H - B - y(EXPENSES[n]!)} className="bar e" style={{ ['--i' as string]: n }} />
              <text x={x + step / 2} y={H - 9} textAnchor="middle" className="ax">{m}</text>
            </g>
          );
        }) : (
          <>
            <path d={`${path} L${pts[5]![0]} ${H - B} L${pts[0]![0]} ${H - B} Z`} className="area" />
            <path d={path} className="line" pathLength={1} />
            {MONTHS.map((m, n) => (
              <g key={m} className={n === sel ? 'on' : ''} tabIndex={0} onMouseEnter={() => setSel(n)} onFocus={() => setSel(n)} onClick={() => setSel(n)}
                aria-label={`${m}: ${RATE[n]} percent collected`}>
                <rect x={L + step * n} y={T} width={step} height={H - T - B} className="hit" />
                <circle cx={pts[n]![0]} cy={pts[n]![1]} r={n === sel ? 6 : 4} className="dot" />
                <text x={pts[n]![0]} y={H - 9} textAnchor="middle" className="ax">{m}</text>
              </g>
            ))}
          </>
        )}
      </svg>
      <div className="sx-readout" aria-live="polite">
        {kind === 'pair' ? (
          <>
            <span>{MONTHS[sel]}</span>
            <span><i className="c" />Collected <b>KES {fmt(COLLECTED[sel]! * 1000)}</b></span>
            <span><i className="e" />Expenses <b>KES {fmt(EXPENSES[sel]! * 1000)}</b></span>
            <span>Net <b>KES {fmt(net(sel) * 1000)}</b></span>
          </>
        ) : (
          <>
            <span>{MONTHS[sel]}</span>
            <span>Collection rate <b>{RATE[sel]!.toFixed(2)}%</b></span>
            {sel > 0 && <span>{RATE[sel]! >= RATE[sel - 1]! ? 'Up' : 'Down'} {Math.abs(RATE[sel]! - RATE[sel - 1]!).toFixed(1)} points on {MONTHS[sel - 1]}</span>}
          </>
        )}
      </div>
      <p className="sx-muted sx-note">Illustrative demo portfolio. Hover, tap or tab to a month.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Steps that explain themselves                                      */
/* ------------------------------------------------------------------ */

export function StepFlow({ steps, startAt = 0 }: { steps: { label: string; text: string }[]; startAt?: number }) {
  const [i, setI] = useState(startAt);
  return (
    <div className="sx-stepflow">
      <ol role="tablist">
        {steps.map((s, n) => (
          <li key={s.label}>
            <button type="button" role="tab" aria-selected={n === i} className={`${n === i ? 'on' : ''} ${n < i ? 'past' : ''}`}
              onClick={() => setI(n)} onMouseEnter={() => setI(n)} onFocus={() => setI(n)}>
              <span className="n" aria-hidden>{n < i ? '✓' : n + 1}</span>{s.label}
            </button>
          </li>
        ))}
      </ol>
      <p key={i} role="tabpanel">{steps[i]!.text}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The tenant portal on a phone                                       */
/* ------------------------------------------------------------------ */

export function PortalPhone() {
  const [tab, setTab] = useState<'Balance' | 'Statement' | 'Repair'>('Balance');
  const [issue, setIssue] = useState('Kitchen tap');
  const [sent, setSent] = useState(false);
  return (
    <div className="sx-phone" aria-label="The tenant portal, as a tenant sees it">
      <div className="sx-phone-top"><span>Amina H.</span><span className="sx-pill pos">Up to date</span></div>
      <div className="sx-phone-tabs" role="tablist">
        {(['Balance', 'Statement', 'Repair'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      <div className="sx-phone-body" key={tab} role="tabpanel">
        {tab === 'Balance' && (
          <>
            <div className="sx-phone-big"><small>Balance</small><b>KES <CountUp to={0} /></b></div>
            <dl>
              <div><dt>Unit</dt><dd>S1</dd></div>
              <div><dt>Rent</dt><dd>KES 85,000 / month</dd></div>
              <div><dt>Last receipt</dt><dd>RCP-000002</dd></div>
            </dl>
          </>
        )}
        {tab === 'Statement' && (
          <dl>
            <div><dt>Rent, September</dt><dd>85,000</dd></div>
            <div><dt>Payment, M-Pesa</dt><dd className="pos">-85,000</dd></div>
            <div><dt>Balance</dt><dd><b>0</b></dd></div>
          </dl>
        )}
        {tab === 'Repair' && !sent && (
          <>
            <div className="sx-phone-chips">
              {['Kitchen tap', 'Light fitting', 'Door lock'].map((x) => (
                <button key={x} type="button" aria-pressed={issue === x} className={issue === x ? 'on' : ''} onClick={() => setIssue(x)}>{x}</button>
              ))}
            </div>
            <button type="button" className="sx-btn full" onClick={() => setSent(true)}>Send the request</button>
          </>
        )}
        {tab === 'Repair' && sent && (
          <>
            <p className="sx-phone-sent"><b>{issue}</b> reported to your landlord.</p>
            <ol className="sx-mini">
              {['Reported', 'Seen', 'Assigned', 'Fixed'].map((x, n) => <li key={x} className={n === 0 ? 'done' : ''}>{x}</li>)}
            </ol>
            <button type="button" className="sx-btn ghost full" onClick={() => setSent(false)}>Raise another</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ask the portfolio                                                  */
/* ------------------------------------------------------------------ */

const ASKS: { q: string; a: string; src: string }[] = [
  { q: 'Who is behind on rent?', a: '5 tenants owe KES 152,300 in total. The oldest is T. Mwangi, over 60 days, KES 62,000.', src: 'Your arrears report' },
  { q: 'Which units are vacant?', a: '2 units are empty: one in Apartment block and one in Townhouse villas. Both can be listed on the marketplace.', src: 'Your units list' },
  { q: 'Which leases expire soon?', a: 'One lease ends within 60 days: Amina H., unit S1. The 60 day reminder has gone out.', src: 'Your lease register' },
  { q: 'How is collection trending?', a: 'Collection is 90.57%, up 5.4 points since June.', src: 'Your rent records' },
];

export function AskDemo() {
  const reduced = useReducedMotion();
  const [q, setQ] = useState(0);
  const [n, setN] = useState(0);
  const ans = ASKS[q]!.a;
  useEffect(() => {
    if (reduced) { setN(ans.length); return; }
    setN(0);
    const t = window.setInterval(() => setN((c) => { if (c >= ans.length) { window.clearInterval(t); return c; } return c + 2; }), 22);
    return () => window.clearInterval(t);
  }, [ans, reduced]);
  const done = n >= ans.length;
  return (
    <div className="sx-card sx-ask">
      <div className="sx-ask-chips" role="group" aria-label="Pick a question">
        {ASKS.map((x, i) => (
          <button key={x.q} type="button" aria-pressed={i === q} className={i === q ? 'on' : ''} onClick={() => setQ(i)}>{x.q}</button>
        ))}
      </div>
      <div className="sx-ask-chat" aria-live="polite">
        <p className="you">{ASKS[q]!.q}</p>
        <p className="bot">{ans.slice(0, n)}{!done && <span className="caret" aria-hidden />}</p>
        {done && <small className="src">Source: {ASKS[q]!.src}</small>}
      </div>
      <p className="sx-muted sx-note">Illustrative demo portfolio. The assistant reads your records and never changes them.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Portfolio size                                                     */
/* ------------------------------------------------------------------ */

export function StageSlider({ stages }: { stages: { range: string; title: string; text: string }[] }) {
  const [units, setUnits] = useState(24);
  const idx = units <= 10 ? 0 : units <= 50 ? 1 : 2;
  const cur = stages[idx]!;
  const shown = Math.min(units, 120);
  return (
    <div className="sx-card sx-stages">
      <label className="sx-slider">
        <span>How many units do you manage? <b>{units >= 150 ? '150+' : units}</b></span>
        <input type="range" min={1} max={150} value={units} onChange={(e) => setUnits(Number(e.target.value))}
          aria-valuetext={`${units} units, ${cur.title}`} />
      </label>
      <div className="sx-dots" aria-hidden>
        {Array.from({ length: shown }, (_, n) => <i key={n} className={`s${n < 10 ? 0 : n < 50 ? 1 : 2}`} />)}
      </div>
      <div className="sx-stage-cards">
        {stages.map((s, n) => (
          <button key={s.title} type="button" className={`sx-stage-card ${n === idx ? 'on' : ''}`} onClick={() => setUnits(n === 0 ? 6 : n === 1 ? 24 : 80)} aria-pressed={n === idx}>
            <span className="r">{s.range}</span><b>{s.title}</b>
            <span>{s.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Before and after                                                   */
/* ------------------------------------------------------------------ */

export function BeforeAfter({ pairs }: { pairs: [string, string][] }) {
  const [after, setAfter] = useState(false);
  return (
    <div className={`sx-ba ${after ? 'after' : 'before'}`}>
      <div className="sx-ba-switch" role="group" aria-label="Compare">
        <button type="button" aria-pressed={!after} className={!after ? 'on' : ''} onClick={() => setAfter(false)}>Before NyumbaSuite</button>
        <button type="button" aria-pressed={after} className={after ? 'on' : ''} onClick={() => setAfter(true)}>With NyumbaSuite</button>
      </div>
      <ul>
        {pairs.map(([b, a], n) => (
          <li key={b} style={{ ['--i' as string]: n }}>
            <span className="mark" aria-hidden>{after ? '✓' : '×'}</span>
            <span key={after ? 'a' : 'b'} className="txt">{after ? a : b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
